import React, { useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  useTheme,
  alpha,
  IconButton,
  Chip,
} from '@mui/material'
import {
  CloudUpload as UploadIcon,
  Folder as FolderIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material'
import { documentsApi } from '../../services/api'
import toast from 'react-hot-toast'

interface UploadExternalDICOMDialogProps {
  open: boolean
  patientId?: string
  patientName?: string
  patientCNP?: string
  onClose: () => void
  onSuccess: () => void
  institutions: Array<{ id: string; name: string }>
}

interface CNPMismatchData {
  dicom_patient: { name: string; patient_id: string }
  ris_patient: { name: string; cnp: string }
  study_info: {
    study_uid: string
    study_date: string
    study_description: string
    modalities: string[]
  }
}

interface UploadResult {
  status: 'success' | 'cnp_mismatch' | 'error'
  message?: string
  patient?: { first_name: string; last_name: string; cnp?: string }
  patient_created?: boolean
  studies_uploaded?: Array<{
    study_uid: string
    study_id: string
    file_count: number
    series_count: number
    study_date: string
    study_description: string
    modalities: string[]
  }>
  invalid_files?: Array<{ file: string; error: string }>
  cnp_mismatch_details?: CNPMismatchData
}

const UploadExternalDICOMDialog: React.FC<UploadExternalDICOMDialogProps> = ({
  open,
  patientId,
  patientName,
  patientCNP: _patientCNP,
  onClose,
  onSuccess,
  institutions,
}) => {
  const theme = useTheme()
  const { designSystem } = theme
  const colors = theme.palette.mode === 'dark' 
    ? designSystem.colors.dark 
    : designSystem.colors.light

  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [institutionId, setInstitutionId] = useState('')
  const [sourceInstitution, setSourceInstitution] = useState('')
  const [notes, setNotes] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [showCNPMismatch, setShowCNPMismatch] = useState(false)
  const [cnpMismatchData, setCnpMismatchData] = useState<CNPMismatchData | null>(null)
  const [confirmationReason, setConfirmationReason] = useState('')
  const [showSuccess, setShowSuccess] = useState(false)
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null)
  const [dragActive, setDragActive] = useState(false)
  
  const isAutoPatientMode = !patientId

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setSelectedFiles(Array.from(e.dataTransfer.files))
    }
  }, [])

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setSelectedFiles(files)
  }

  const handleFolderSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setSelectedFiles(files)
  }

  const handleUpload = async (cnpConfirmed = false) => {
    if (selectedFiles.length === 0) {
      toast.error('Selectați cel puțin un fișier DICOM')
      return
    }

    if (!institutionId) {
      toast.error('Selectați instituția')
      return
    }

    try {
      setUploading(true)
      setUploadProgress(10)

      let result
      
      if (isAutoPatientMode) {
        result = await documentsApi.uploadExternalDicomAuto(
          selectedFiles,
          institutionId,
          sourceInstitution || undefined,
          notes || undefined
        )
      } else {
        result = await documentsApi.uploadExternalDicom(
          patientId!,
          selectedFiles,
          institutionId,
          sourceInstitution || undefined,
          notes || undefined,
          cnpConfirmed,
          cnpConfirmed ? confirmationReason : undefined
        )
      }

      setUploadProgress(90)

      if (result.status === 'cnp_mismatch') {
        setCnpMismatchData(result.cnp_mismatch_details)
        setShowCNPMismatch(true)
        setUploading(false)
        setUploadProgress(0)
        return
      }

      if (result.status === 'success') {
        setUploadProgress(100)
        setUploadResult(result)
        setShowSuccess(true)
        
        if (isAutoPatientMode && result.patient) {
          const patientCreatedMsg = result.patient_created 
            ? `Pacient nou creat: ${result.patient.first_name} ${result.patient.last_name}`
            : `Pacient găsit: ${result.patient.first_name} ${result.patient.last_name}`
          toast.success(`Upload completat! ${result.studies_uploaded?.length || 0} studii. ${patientCreatedMsg}`)
        } else {
          toast.success(`Upload completat! ${result.studies_uploaded?.length || 0} studii`)
        }
        onSuccess()
      }

    } catch (error: any) {
      console.error('Eroare upload DICOM extern:', error)
      
      if (!isAutoPatientMode && error.response?.status === 409 && error.response?.data?.status === 'cnp_mismatch') {
        setCnpMismatchData(error.response.data)
        setShowCNPMismatch(true)
        setUploading(false)
        setUploadProgress(0)
        return
      }
      
      toast.error(error.response?.data?.detail || error.response?.data?.message || 'Eroare la upload DICOM')
      setUploading(false)
      setUploadProgress(0)
    }
  }

  const handleCNPConfirmation = () => {
    if (!confirmationReason.trim()) {
      toast.error('Specificați motivul confirmării')
      return
    }
    setShowCNPMismatch(false)
    handleUpload(true)
  }

  const handleClose = () => {
    if (!uploading) {
      setSelectedFiles([])
      setInstitutionId('')
      setSourceInstitution('')
      setNotes('')
      setConfirmationReason('')
      setShowCNPMismatch(false)
      setShowSuccess(false)
      setCnpMismatchData(null)
      setUploadResult(null)
      onClose()
    }
  }

  // Dialog styles
  const dialogPaperStyle = {
    backgroundColor: colors.background.paper,
    backgroundImage: 'none',
    borderRadius: 2,
  }

  // Success Dialog
  if (showSuccess && uploadResult) {
    return (
      <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }} disableEscapeKeyDown maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperStyle }}>
        <DialogTitle sx={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 1.5, px: 2, borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: alpha(designSystem.colors.status.success, 0.1)
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SuccessIcon sx={{ color: designSystem.colors.status.success, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.9rem' }}>
              Upload Completat
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: colors.text.secondary }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {/* Patient info for auto-create */}
          {isAutoPatientMode && uploadResult.patient && (
            <Box sx={{ 
              mb: 1.5, p: 1.5, borderRadius: 1,
              backgroundColor: alpha(colors.accent.primary, 0.1),
              border: `1px solid ${alpha(colors.accent.primary, 0.3)}`
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: colors.accent.primary }}>
                {uploadResult.patient_created ? 'Pacient nou creat:' : 'Pacient găsit:'}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary, fontWeight: 500 }}>
                {uploadResult.patient.first_name} {uploadResult.patient.last_name}
                {uploadResult.patient.cnp && ` (${uploadResult.patient.cnp})`}
              </Typography>
            </Box>
          )}

          {/* Studies list */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text.secondary, textTransform: 'uppercase' }}>
            Studii create: {uploadResult.studies_uploaded?.length || 0}
          </Typography>
          
          {uploadResult.studies_uploaded?.map((study, idx) => (
            <Box key={idx} sx={{ 
              mt: 1, p: 1.5, borderRadius: 1,
              backgroundColor: colors.background.elevated,
              border: `1px solid ${colors.border.default}`
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.8rem' }}>
                {study.study_description || 'Fără descriere'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <Chip label={`${study.file_count} fișiere`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                <Chip label={`${study.series_count} serii`} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                <Chip label={study.modalities.join(', ')} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                <Chip label={new Date(study.study_date).toLocaleDateString('ro-RO')} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              </Box>
            </Box>
          ))}

          {uploadResult.invalid_files && uploadResult.invalid_files.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
              {uploadResult.invalid_files.length} fișiere ignorate (DICOMDIR, viewer, etc.)
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border.default}` }}>
          <Button onClick={handleClose} variant="contained" size="small" sx={{ textTransform: 'none' }}>
            Închide
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  // CNP Mismatch Dialog
  if (showCNPMismatch && cnpMismatchData) {
    return (
      <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }} disableEscapeKeyDown maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperStyle }}>
        <DialogTitle sx={{ 
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          py: 1.5, px: 2, borderBottom: `1px solid ${colors.border.default}`,
          backgroundColor: alpha(designSystem.colors.status.warning, 0.1)
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: designSystem.colors.status.warning, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.9rem' }}>
              CNP Diferit Detectat
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} sx={{ color: colors.text.secondary }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            CNP-ul din DICOM nu corespunde cu pacientul din RIS!
          </Alert>

          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
            <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: colors.background.elevated, border: `1px solid ${colors.border.default}` }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text.secondary }}>DICOM</Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary, fontSize: '0.8rem' }}>
                ID: {cnpMismatchData.dicom_patient.patient_id}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary, fontSize: '0.8rem' }}>
                Nume: {cnpMismatchData.dicom_patient.name}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1.5, borderRadius: 1, backgroundColor: colors.background.elevated, border: `1px solid ${colors.border.default}` }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text.secondary }}>RIS</Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary, fontSize: '0.8rem' }}>
                CNP: {cnpMismatchData.ris_patient.cnp}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.primary, fontSize: '0.8rem' }}>
                Nume: {cnpMismatchData.ris_patient.name}
              </Typography>
            </Box>
          </Box>

          <TextField
            fullWidth
            required
            multiline
            rows={2}
            size="small"
            label="Motiv confirmare"
            placeholder="Ex: CD de la alt spital cu CNP vechi..."
            value={confirmationReason}
            onChange={(e) => setConfirmationReason(e.target.value)}
            sx={{ 
              '& .MuiInputBase-root': { backgroundColor: colors.background.elevated },
              '& .MuiInputLabel-root': { color: colors.text.secondary },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border.default}` }}>
          <Button onClick={handleClose} disabled={uploading} size="small" sx={{ textTransform: 'none', color: colors.text.secondary }}>
            Anulează
          </Button>
          <Button
            onClick={handleCNPConfirmation}
            variant="contained"
            color="warning"
            size="small"
            disabled={uploading || !confirmationReason.trim()}
            sx={{ textTransform: 'none' }}
          >
            Confirmă Upload
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  // Main Upload Dialog
  return (
    <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }} disableEscapeKeyDown maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperStyle }}>
      <DialogTitle sx={{ 
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        py: 1.5, px: 2, borderBottom: `1px solid ${colors.border.default}`
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon sx={{ color: colors.accent.primary, fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.9rem' }}>
            {isAutoPatientMode ? 'Încarcă Examen' : `Încarcă Examen - ${patientName}`}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} disabled={uploading} sx={{ color: colors.text.secondary }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {/* Institution Select */}
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel sx={{ color: colors.text.secondary }}>Instituție *</InputLabel>
          <Select
            value={institutionId}
            onChange={(e) => setInstitutionId(e.target.value)}
            label="Instituție *"
            sx={{ 
              backgroundColor: colors.background.elevated,
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default },
              '& .MuiSelect-select': { color: colors.text.primary }
            }}
          >
            {institutions.map(inst => (
              <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Source & Notes - compact row */}
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            label="Proveniență"
            placeholder="Ex: CD Regina Maria"
            value={sourceInstitution}
            onChange={(e) => setSourceInstitution(e.target.value)}
            sx={{ 
              flex: 1,
              '& .MuiInputBase-root': { backgroundColor: colors.background.elevated },
              '& .MuiInputLabel-root': { color: colors.text.secondary, fontSize: '0.85rem' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default }
            }}
          />
          <TextField
            size="small"
            label="Note"
            placeholder="Opțional..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ 
              flex: 1,
              '& .MuiInputBase-root': { backgroundColor: colors.background.elevated },
              '& .MuiInputLabel-root': { color: colors.text.secondary, fontSize: '0.85rem' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.border.default }
            }}
          />
        </Box>

        {/* Drag & Drop Zone */}
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragActive ? colors.accent.primary : colors.border.default}`,
            borderRadius: 1.5,
            p: 2,
            textAlign: 'center',
            backgroundColor: dragActive 
              ? alpha(colors.accent.primary, 0.1) 
              : selectedFiles.length > 0 
                ? alpha(designSystem.colors.status.success, 0.05)
                : colors.background.elevated,
            transition: 'all 0.2s',
            cursor: 'pointer',
            '&:hover': {
              borderColor: colors.accent.primary,
              backgroundColor: alpha(colors.accent.primary, 0.05),
            }
          }}
        >
          {selectedFiles.length > 0 ? (
            <Box>
              <FileIcon sx={{ fontSize: 32, color: designSystem.colors.status.success, mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.85rem' }}>
                {selectedFiles.length} fișiere selectate
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                Click pentru a schimba selecția
              </Typography>
            </Box>
          ) : (
            <Box>
              <UploadIcon sx={{ fontSize: 32, color: colors.accent.primary, mb: 0.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: colors.text.primary, fontSize: '0.85rem' }}>
                Drag & Drop sau click pentru a selecta
              </Typography>
              <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                DICOM, ZIP sau folder întreg
              </Typography>
            </Box>
          )}
        </Box>

        {/* File/Folder Selection Buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<UploadIcon sx={{ fontSize: 16 }} />}
            sx={{ 
              flex: 1, textTransform: 'none', fontSize: '0.75rem',
              borderColor: colors.border.default, color: colors.text.secondary,
              '&:hover': { borderColor: colors.accent.primary, color: colors.accent.primary }
            }}
          >
            Fișiere
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileChange}
              accept=".dcm,.DCM,.dicom,.DICOM,.zip,.ZIP,application/zip"
            />
          </Button>
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<FolderIcon sx={{ fontSize: 16 }} />}
            sx={{ 
              flex: 1, textTransform: 'none', fontSize: '0.75rem',
              borderColor: colors.border.default, color: colors.text.secondary,
              '&:hover': { borderColor: colors.accent.primary, color: colors.accent.primary }
            }}
          >
            Folder
            <input
              type="file"
              hidden
              // @ts-ignore
              webkitdirectory=""
              directory=""
              multiple
              onChange={handleFolderSelection}
            />
          </Button>
        </Box>

        {/* Auto-create patient info */}
        {isAutoPatientMode && (
          <Typography variant="caption" sx={{ 
            display: 'block', mt: 1, color: colors.accent.primary, fontWeight: 500, textAlign: 'center'
          }}>
            Pacientul va fi creat automat din datele DICOM
          </Typography>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 1.5 }}>
            <LinearProgress 
              variant="determinate" 
              value={uploadProgress} 
              sx={{ 
                height: 6, borderRadius: 3,
                backgroundColor: alpha(colors.accent.primary, 0.2),
                '& .MuiLinearProgress-bar': { backgroundColor: colors.accent.primary }
              }}
            />
            <Typography variant="caption" sx={{ color: colors.text.secondary, mt: 0.5, display: 'block', textAlign: 'center' }}>
              Se procesează... {uploadProgress}%
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${colors.border.default}` }}>
        <Button onClick={handleClose} disabled={uploading} size="small" sx={{ textTransform: 'none', color: colors.text.secondary }}>
          Anulează
        </Button>
        <Button
          onClick={() => handleUpload(false)}
          variant="contained"
          size="small"
          disabled={selectedFiles.length === 0 || !institutionId || uploading}
          startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon sx={{ fontSize: 16 }} />}
          sx={{ textTransform: 'none' }}
        >
          {uploading ? 'Se uploadează...' : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadExternalDICOMDialog
